import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Title, Body } from "@vds/typography";
import { Loader } from "@vds/loaders";
import { Button } from "@vds/buttons";
import BreadcrumbsContainer from "../../../global/components/BreadcrumbsContainer";
import { Notification } from "@vds/notifications";
import { Modal } from "@vds/modals";
import {
  fetchAppearancesRequest,
  deleteAppearanceRequest,
  setSelectedAppearance,
  clearAppearanceError,
  downloadAppearanceImagesRequest,
  resetCreateStates,
  deleteAppearanceImageRequest,
  retryFailedImageRequest
} from "../../../store/actions/ea-actions";
import { fetchLicenses } from "../../../store/actions/license-actions";
import { showNotificationBanner } from "../../../store/actions";
import { notificationTypes } from "../../../layouts/NotificationConfig";
import {
  APPEARANCE_TEXT,
  LICENSE_MESSAGES,
  PLACEHOLDER_IMAGE,
  SITES_TEXT,
  APPEARANCE_RUNTIME_STATUS,
  STATUS_BADGE_TEXT_COLORS,
  IMAGE_STATUS_ICON_BG_COLORS,
  CREATE_APPEARANCE,
  BADGE_COLORS
} from "../../../constants/enrolledAppearanceConstants";
import MuiTooltip from "@mui/material/Tooltip";
import { LICENSE_SKU_PATTERNS, LICENSE_STATUS } from "../../../global/constants/licensesConstants";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import SlickImageCarousel from "../../../global/components/imageCarousel/SlickImageCarousel";
import ErrorTemplate from "../../../global/components/errorTemplate/ErrorTemplate";
import placeholderImg from "../../../assets/placeholder.png";
import {
  AppearanceListContainer,
  BreadcrumbWrapper,
  AppearanceListHeader,
  AppearanceGrid,
  AppearanceCard as StyledAppearanceCard,
  AppearanceCardHeader,
  AppearanceLeftSection,
  AppearanceBadgesRow,
  AppearanceActions,
  IconButtons,
  ImageStatusList,
  ImageThumbnail,
  ImageStatusIconList,
  ImageDeleteButton,
  ImageStatusBadge,
  ViewStreamsLink,
  ViewStreamsRow,
  SitesSection,
  SitesLabel,
  SitesList,
  SiteTag,
  SitesIntroText,
  ModalHeader,
  ImageEmptyPlaceholder,
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  LoaderContainer,
  NotificationWrapper,
  PageTitle,
  SubtitleRow,
  LegendContainer,
  LegendItem,
  LegendIconBadge,
  TabsWrapper,
  DeploymentLockLink,
  LazyImageFetchTrigger
} from "./styles/ea-styles";
import { ROUTES } from "../../../global/constants/routesConstants";
import DeployModal from "../components/DeployModal";
import ImageGuidelinesModal from "../components/ImageGuidelinesModal";
import ErrorModal from "../components/ErrorModal";

import "./styles/styles.css";
import { Icon } from "@vds/icons";
import { Tab, Tabs } from "@vds/tabs";
import { USER_ROLE } from "../../../global/constants/constants";
import useUserRole from "../../../global/hooks/useUserRole";
import useLazyImageLoad from "../../../global/hooks/useLazyImageLoad";
import CrossSiteWarningModal from "../components/CrossSiteWarningModal";
import { ImageDownloadErrorWrapper, ImageDownloadErrorContent, ImageDownloadErrorRefresh } from "../components/ImageDownloadError";

const LEGEND_ITEMS = [
  { status: APPEARANCE_RUNTIME_STATUS.INVALID, label: CREATE_APPEARANCE.INVALID_IMAGES_LABEL, icon: "trash" },
  { status: APPEARANCE_RUNTIME_STATUS.VALID, label: CREATE_APPEARANCE.VALID_IMAGES_LABEL, icon: "checkmark" },
  { status: APPEARANCE_RUNTIME_STATUS.VALIDATING, label: CREATE_APPEARANCE.VALIDATING_IMAGES_LABEL, icon: "timer" }
];

const STATUS_ICONS = {
  [APPEARANCE_RUNTIME_STATUS.VALIDATING]: "timer",
  [APPEARANCE_RUNTIME_STATUS.NEW]: "timer",
  [APPEARANCE_RUNTIME_STATUS.VALID]: "checkmark"
};

const LazyImageFetcher = ({ appearanceId, images }) => {
  const ref = useLazyImageLoad(appearanceId, images);
  return <LazyImageFetchTrigger ref={ref} />;
};

const EAList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    appearances,
    loading,
    error,
    deleteLoading,
    deleteSuccess,
    deleteError,
    imageCache,
    imageLoadingStates,
    updateSuccess,
    imageDeleteStatus,
    retryingImageIds
  } = useSelector((state) => state.enrolledAppearanceReducer);
  const licenses = useSelector((state) => state.licenseReducer.licenses);

  const normalizeAppearances = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.appearances)) return data.appearances;
    return [];
  };

  const appearanceList = normalizeAppearances(appearances);

  // Local state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [appearanceToDelete, setAppearanceToDelete] = useState(null);
  const [imageModalTitle, setImageModalTitle] = useState("");
  // Delete image confirmation modal state
  const [isImageDeleteModalOpen, setIsImageDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState({ appearanceId: null, imageId: null });
  // Deploy modal
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showCrossSiteWarning, setShowCrossSiteWarning] = useState(false);
  const crossSiteAppearance = useRef(null);
  const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false);
  // { type: 'delete'|'unenroll', appearanceName: string, appearance?: object } | null
  const [deploymentBlockModal, setDeploymentBlockModal] = useState(null);
  const [showEnrolledSitesModal, setShowEnrolledSitesModal] = useState(false);
  const [enrolledSitesAppearance, setEnrolledSitesAppearance] = useState(null);

  // State for Image Preview Modal
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedSites, setSelectedSites] = useState(new Set());
  const sitesTagList = useSelector((state) => state.sitesReducer.siteTagList);
  const { standardizedRole: standardizedUserRole } = useUserRole();
  const ROLE_ADMIN = USER_ROLE.ADMIN.toLowerCase();
  const ROLE_SITE_VIEWER = USER_ROLE.SITE_VIEWER.toLowerCase();
  const ROLE_SITE_MANAGER = USER_ROLE.SITE_MANAGER.toLowerCase();
  const siteList = sitesTagList?.sites || [];
  const userSiteIds = useMemo(() => new Set(siteList.map((s) => s.id)), [siteList]);
  const isMyEA = useCallback((appearance) => (appearance.enrolledSiteIds || []).some((id) => userSiteIds.has(id)), [userSiteIds]);
  // Track first-time image load failures to avoid repeated re-download attempts
  const currentAppearance = useRef({
    appearanceName: "",
    appearanceId: "",
    enrolledSiteIds: [],
    appearanceStatus: "",
    deployments: []
  });

  // Helper function to check if images are loading for a specific appearance
  const checkIsImagesLoading = (appearanceId) => {
    return imageLoadingStates[appearanceId]?.loading;
  };

  // Helper: compute badge display props for an appearance card
  const isCreatedWithin24Hours = (appearance) => {
    const createdOnValue = appearance?.createdOn || appearance?.createdon;
    if (!createdOnValue) return false;
    return Date.now() - new Date(createdOnValue).getTime() < 86400000;
  };

  const getAppearanceBadgeProps = (appearance) => {
    const showErrorBadge = appearance.status === APPEARANCE_RUNTIME_STATUS.INVALID;
    const errorCount = showErrorBadge
      ? (appearance.images || []).filter((img) => img.status === APPEARANCE_RUNTIME_STATUS.INVALID || img.status === APPEARANCE_RUNTIME_STATUS.FAILED).length
      : 0;

    if (showErrorBadge) {
      const errorSuffix = errorCount === 1 ? APPEARANCE_TEXT.ERROR_FOUND : APPEARANCE_TEXT.ERRORS_FOUND;
      const badgeLabel = errorCount === 0 ? CREATE_APPEARANCE.INVALID_IMAGES_LABEL : `${errorCount} ${errorSuffix}`;
      return {
        showErrorBadge,
        errorCount,
        badgeTextColor: STATUS_BADGE_TEXT_COLORS.errors,
        badgeLabel,
        tooltipText: null
      };
    }

    const deployments = appearance.deployments || [];

    // Out of Sync: any deployment usedAppearances version differs from top-level embeddingVersion
    const appearanceEmbeddingVersion = appearance.embeddingVersion;
    const isOutOfSync =
      appearanceEmbeddingVersion != null &&
      deployments.some((d) => {
        const used = d?.config?.usedAppearances || {};
        return Object.values(used).some((u) => u.embeddingVersion != null && u.embeddingVersion !== appearanceEmbeddingVersion);
      });

    const getStatusOnlyBadge = (s) => {
      if (s === APPEARANCE_RUNTIME_STATUS.SUBMITTED) {
        return {
          badgeLabel: APPEARANCE_TEXT.PROCESSING_LABEL,
          badgeBg: BADGE_COLORS.BG_PROCESSING,
          badgeTextColor: BADGE_COLORS.TEXT_BLACK,
          tooltipText: null
        };
      }
      if (s === APPEARANCE_RUNTIME_STATUS.ACTIVE) {
        return {
          badgeLabel: APPEARANCE_TEXT.ACTIVE_LABEL,
          badgeBg: BADGE_COLORS.BG_ACTIVE,
          badgeTextColor: BADGE_COLORS.TEXT_WHITE,
          tooltipText: APPEARANCE_TEXT.ACTIVE_TOOLTIP
        };
      }
      if (s === APPEARANCE_RUNTIME_STATUS.VALID) {
        return { badgeLabel: APPEARANCE_TEXT.VALID_STATUS_LABEL, badgeBg: BADGE_COLORS.BG_VALID, badgeTextColor: BADGE_COLORS.TEXT_BLACK, tooltipText: null };
      }
      const badgeTextColor = STATUS_BADGE_TEXT_COLORS[s] || BADGE_COLORS.TEXT_BLACK;
      return { badgeLabel: s || APPEARANCE_TEXT.DEFAULT_STATUS, badgeTextColor, badgeBg: null, tooltipText: null };
    };

    if (isOutOfSync) {
      return {
        showErrorBadge: false,
        errorCount: 0,
        badgeTextColor: BADGE_COLORS.TEXT_WHITE,
        badgeLabel: APPEARANCE_TEXT.OUT_OF_SYNC_LABEL,
        tooltipText: APPEARANCE_TEXT.OUT_OF_SYNC_TOOLTIP,
        badgeBg: BADGE_COLORS.BG_OUT_OF_SYNC,
        secondaryBadge: getStatusOnlyBadge(appearance.status)
      };
    }

    return { showErrorBadge: false, errorCount: 0, ...getStatusOnlyBadge(appearance.status) };
  };

  const hasActiveLicenses = useMemo(() => {
    if (!licenses || !Array.isArray(licenses) || licenses.length === 0) {
      return false;
    }
    return licenses.some((license) => license.state?.toLowerCase() === LICENSE_STATUS.ACTIVE && license.sku === LICENSE_SKU_PATTERNS.ENROLLED_APPEARANCE_V2);
  }, [licenses]);

  // Fetch appearances on component mount
  useEffect(() => {
    dispatch(fetchAppearancesRequest({}));
  }, [dispatch]);

  // Fetch licenses if not already available
  useEffect(() => {
    if (!licenses || licenses.length === 0) {
      try {
        dispatch(fetchLicenses());
      } catch (error) {
        const notificationInfo = {
          type: "Error",
          priority: "critical",
          showNotification: true,
          message: LICENSE_MESSAGES.LICENSE_FETCH_ERROR
        };
        dispatch(showNotificationBanner(notificationInfo));
      }
    }
  }, [licenses, dispatch]);

  // Handle delete success/failure
  useEffect(() => {
    if (deleteSuccess) {
      setDeleteModalOpen(false);
      setAppearanceToDelete(null);
      dispatch(
        showNotificationBanner({
          type: notificationTypes[60],
          showNotification: true,
          priority: "success",
          isDismissible: true,
          title: APPEARANCE_TEXT.DELETE_SUCCESS
        })
      );
      dispatch(clearAppearanceError());
    }
    if (deleteError) {
      setDeleteModalOpen(false);
      setAppearanceToDelete(null);
      dispatch(
        showNotificationBanner({
          type: notificationTypes[61],
          subtitle: deleteError,
          showNotification: true,
          priority: "critical",
          isDismissible: true,
          title: APPEARANCE_TEXT.DELETE_ERROR
        })
      );
      dispatch(clearAppearanceError());
    }
  }, [deleteSuccess, deleteError]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(resetCreateStates());
    }
  }, [updateSuccess]);

  // Handle edit appearance
  const handleEdit = (appearance) => {
    if (standardizedUserRole === ROLE_SITE_MANAGER && !isMyEA(appearance)) {
      crossSiteAppearance.current = appearance;
      setShowCrossSiteWarning(true);
      return;
    }
    dispatch(setSelectedAppearance(appearance));
    navigate(`${ROUTES.EDIT_APPEARANCE}?id=${appearance.id}`);
  };

  const handleCrossSiteEnroll = () => {
    const app = crossSiteAppearance.current;
    setShowCrossSiteWarning(false);
    currentAppearance.current.appearanceName = app.name;
    currentAppearance.current.appearanceId = app.id;
    currentAppearance.current.enrolledSiteIds = app?.enrolledSiteIds || [];
    currentAppearance.current.appearanceStatus = app?.status || "";
    currentAppearance.current.deployments = app?.deployments || [];
    currentAppearance.current.isOtherEA = !isMyEA(app);
    setShowDeployModal(true);
  };

  const getDeployModalTagids = () => {
    if (standardizedUserRole !== ROLE_SITE_MANAGER) {
      return currentAppearance.current.enrolledSiteIds;
    }
    if (currentAppearance.current.isOtherEA) {
      return [];
    }
    return (currentAppearance.current.enrolledSiteIds || []).filter((id) => userSiteIds.has(id));
  };

  // Handle delete appearance
  const handleDelete = (appearanceId) => {
    const appearance = appearanceList.find((app) => app.id === appearanceId);
    if (!appearance) return;
    if (appearance.deployments?.length > 0) {
      setDeploymentBlockModal({ type: "delete", appearanceName: appearance.name, appearance });
      return;
    }
    setAppearanceToDelete(appearance);
    setDeleteModalOpen(false);
    requestAnimationFrame(() => {
      setDeleteModalOpen(true);
    });
  };

  // Handle delete image
  const handleDeleteImage = (e, appearanceId, imageId) => {
    e.stopPropagation();
    dispatch(deleteAppearanceImageRequest({ imageId, appearanceId, shouldRefreshList: true }));
  };

  // Handle edit image
  const handleEditImage = (appearanceId, imageId) => {
    console.log("Edit image:", appearanceId, imageId);
    // TODO: Implement edit image functionality
  };

  // Handle view streams
  const handleViewStreams = (appearance) => {
    if (!appearance || !appearance.id) {
      console.error("Invalid appearance data for navigation");
      return;
    }
    navigate(ROUTES.APPEARANCE_DEPLOYMENTS, { state: { appearance } });
  };
  // Handle create new appearance
  const handleCreateAppearance = () => {
    navigate(ROUTES.CREATE_APPEARANCE);
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    if (appearanceToDelete) {
      dispatch(deleteAppearanceRequest(appearanceToDelete.id));
    }
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setAppearanceToDelete(null);
    if (deleteError) {
      dispatch(clearAppearanceError());
    }
  };

  // Refresh data
  const handleRefresh = () => {
    dispatch(clearAppearanceError());
    dispatch(fetchAppearancesRequest({}));
  };

  // Handle Image Click to open modal
  const handleImageClick = (appearanceId, images, title, index) => {
    // Get cached blob URLs for the images
    const cachedImages = imageCache[appearanceId] || {};

    // Map images to include blob URLs from cache
    const imagesWithUrls = images.map((image) => ({
      ...image,
      url: cachedImages[image.imageId] || image.url || placeholderImg
    }));

    setImageModalTitle(title);
    setSelectedImages(imagesWithUrls);
    setCurrentImageIndex(index);
    setImageModalOpen(false);
    requestAnimationFrame(() => {
      setImageModalOpen(true);
    });
  };

  // Close Image Modal
  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImages([]);
    setCurrentImageIndex(0);
  };

  // Navigate to next image in modal
  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % selectedImages.length);
  };

  // Navigate to previous image in modal
  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + selectedImages.length) % selectedImages.length);
  };

  // Helper function to get site names from siteids
  const getSiteNames = (siteids) => {
    if (!siteids || siteids.length === 0 || !sitesTagList || sitesTagList.length === 0) {
      return [];
    }

    return siteids
      .map((siteId) => {
        const site = sitesTagList?.sites.find((s) => s.id === siteId);
        return site ? site.name : null;
      })
      .filter(Boolean); // Remove null values
  };

  // Get all unique site names from all appearances
  const getAllUniqueSiteNames = () => {
    const allSiteNames = new Set();
    appearances.forEach((appearance) => {
      if (appearance.siteids && appearance.siteids.length > 0) {
        const siteNames = getSiteNames(appearance.siteids);
        siteNames.forEach((name) => allSiteNames.add(name));
      }
    });
    return Array.from(allSiteNames);
  };

  // Helper: parse timestamp from common fields safely
  const parseTimestamp = (val) => {
    if (!val) return 0;
    if (typeof val === "number" && val > 0) return val;
    const t = Date.parse(val);
    return Number.isNaN(t) ? 0 : t;
  };

  // Helper: get an appearance's last updated timestamp (fallbacks applied)
  const getAppearanceLastUpdated = (appearance) => {
    if (!appearance) return 0;
    const candidates = [
      appearance.lastupdated,
      appearance.lastUpdated,
      appearance.updatedAt,
      appearance.updated_on,
      appearance.updatedOn,
      appearance.modified,
      appearance.modifiedAt,
      appearance.createdon,
      appearance.createdOn
    ].map(parseTimestamp);

    // Consider images timestamps if present
    const imageTimes = Array.isArray(appearance.images)
      ? appearance.images.map((img) => {
          const timestamp = img?.updatedAt || img?.lastupdated || img?.createdon || img?.createdOn;
          return parseTimestamp(timestamp);
        })
      : [];

    const maxCandidate = Math.max(0, ...candidates, ...(imageTimes.length ? imageTimes : [0]));
    return maxCandidate;
  };

  // Initialize selected sites with all unique sites when appearances load
  useEffect(() => {
    if (appearances.length > 0) {
      const uniqueSites = getAllUniqueSiteNames();
      setSelectedSites(new Set(uniqueSites));
    }
  }, [appearances, sitesTagList]);

  // Handle site checkbox toggle
  const handleSiteToggle = (siteName) => {
    setSelectedSites((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(siteName)) {
        newSet.delete(siteName);
      } else {
        newSet.add(siteName);
      }
      return newSet;
    });
  };

  // Compute filtered appearances based on selected sites
  const filteredAppearances = React.useMemo(() => {
    if (!Array.isArray(appearanceList) || appearanceList.length === 0) return [];
    const sorted = [...appearanceList].sort((a, b) => getAppearanceLastUpdated(b) - getAppearanceLastUpdated(a));
    if (standardizedUserRole === ROLE_SITE_VIEWER) return sorted.filter(isMyEA);
    if (standardizedUserRole === ROLE_SITE_MANAGER) {
      return activeTab === 0 ? sorted.filter(isMyEA) : sorted.filter((a) => !isMyEA(a));
    }
    return sorted;
  }, [appearanceList, selectedSites, sitesTagList, standardizedUserRole, activeTab, userSiteIds, isMyEA]);

  const renderImageStatusIcon = (image, cachedEntry, appearance) => {
    const canEdit = standardizedUserRole !== ROLE_SITE_VIEWER;

    if (image.status === APPEARANCE_RUNTIME_STATUS.FAILED) {
      const icon = <Icon name="carryover-data" size="XLarge" color={BADGE_COLORS.TEXT_WHITE} ariaHidden={true} />;
      return canEdit ? (
        <ImageDeleteButton
          onClick={(e) => {
            e.stopPropagation();
            dispatch(
              retryFailedImageRequest({
                imageId: image.imageId,
                appearanceId: appearance.id,
                blobUrl: cachedEntry?.blobUrl,
                imageName: cachedEntry?.imageName
              })
            );
          }}
        >
          {icon}
        </ImageDeleteButton>
      ) : (
        <span>{icon}</span>
      );
    }

    if (image.status === APPEARANCE_RUNTIME_STATUS.INVALID) {
      const deleteIcon = canEdit ? (
        <ImageDeleteButton onClick={(e) => handleDeleteImage(e, appearance.id, image.imageId)}>
          <Icon name="trash" size="XLarge" color={BADGE_COLORS.TEXT_WHITE} ariaHidden={true} />
        </ImageDeleteButton>
      ) : (
        <span>
          <Icon name="trash" size="XLarge" color={BADGE_COLORS.TEXT_WHITE} ariaHidden={true} />
        </span>
      );
      return (
        <MuiTooltip
          placement="top"
          arrow
          slotProps={{ tooltip: { sx: { backgroundColor: "white", border: "1px solid black", "& .MuiTooltip-arrow": { backgroundColor: "white" } } } }}
          title={
            <div>
              <Body viewport="desktop" size="small" bold={true}>
                {APPEARANCE_TEXT.INVALID_IMAGE_TOOLTIP_TITLE}
              </Body>
              <Body size="small" viewport="desktop">
                {APPEARANCE_TEXT.INVALID_IMAGE_TOOLTIP_DESC}
                <span
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsGuidelinesOpen(true);
                  }}
                >
                  {APPEARANCE_TEXT.INVALID_IMAGE_GUIDELINES_LINK}
                </span>
                .
              </Body>
            </div>
          }
        >
          {deleteIcon}
        </MuiTooltip>
      );
    }

    if (STATUS_ICONS[image.status]) {
      return <Icon name={STATUS_ICONS[image.status]} size="XLarge" color={BADGE_COLORS.TEXT_WHITE} ariaHidden={true} />;
    }

    return null;
  };

  // Section: render images/thumbnails with status and hover actions
  const renderAppearanceImagesSection = (appearance) => {
    const isImagesLoading = checkIsImagesLoading(appearance.id);
    const isImagesDownloadFailed = imageLoadingStates[appearance.id]?.error;

    return (
      <ImageStatusList data-testid="appearance-images">
        {appearance.images && appearance.images.length > 0 ? (
          appearance.images.map((image, index) => {
            const cachedImages = imageCache[appearance.id] || {};
            const imageKey = image.imageId;
            const cachedEntry = cachedImages[imageKey];
            const imageUrl = cachedEntry?.blobUrl || null;
            const isThisImageDeleting = imageDeleteStatus[image.imageId]?.status === "deleting";
            const isThisImageDownloadFailed = isImagesDownloadFailed && !cachedEntry?.blobUrl;

            if (isThisImageDownloadFailed) {
              return (
                <ImageDownloadErrorWrapper key={image.id || index} data-testid={`image-error-${index}`}>
                  <ImageDownloadErrorContent>
                    <Icon name="error" size="small" color="#000000" />
                    <span>{APPEARANCE_TEXT.IMAGE_FAILED_TO_LOAD}</span>
                  </ImageDownloadErrorContent>
                  <ImageDownloadErrorRefresh
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(downloadAppearanceImagesRequest(appearance.id, [image]));
                    }}
                  >
                    <Icon name="sync" size="small" color="#000000" />
                  </ImageDownloadErrorRefresh>
                </ImageDownloadErrorWrapper>
              );
            }

            return (
              <ImageThumbnail
                key={image.id || index}
                data-testid={`image-thumbnail-${index}`}
                onClick={() => handleImageClick(appearance.id, appearance.images, appearance.name, index)}
              >
                {isImagesLoading || isThisImageDeleting || retryingImageIds?.[image.imageId] ? (
                  <LoaderContainer>
                    <Loader active={true} />
                  </LoaderContainer>
                ) : imageUrl ? (
                  <img src={imageUrl} alt={`${APPEARANCE_TEXT.IMAGE_ALT_TEXT} ${index + 1}`} />
                ) : (
                  <ImageEmptyPlaceholder />
                )}

                {!retryingImageIds?.[image.imageId] && (
                  <ImageStatusIconList status={image.status} data-testid={`image-status-${index}`}>
                    {renderImageStatusIcon(image, cachedEntry, appearance)}
                  </ImageStatusIconList>
                )}
              </ImageThumbnail>
            );
          })
        ) : (
          <ImageEmptyPlaceholder data-testid="no-images-placeholder">{APPEARANCE_TEXT.NO_IMAGE_UPLOADED}</ImageEmptyPlaceholder>
        )}
      </ImageStatusList>
    );
  };

  // Render loading state
  if (loading && appearances.length === 0) {
    return (
      <AppearanceListContainer>
        <BreadcrumbWrapper>
          <BreadcrumbsContainer
            config={[
              { navigateTo: "/settings", breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_SETTINGS },
              { breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_ENROLLED_APPEARANCE }
            ]}
          />
        </BreadcrumbWrapper>
        <LoaderContainer>
          <Loader active={true} fullscreen={false} />
        </LoaderContainer>
      </AppearanceListContainer>
    );
  }

  // Render error state
  if (error && appearances.length === 0) {
    return (
      <AppearanceListContainer className="ea-list">
        <BreadcrumbWrapper>
          <BreadcrumbsContainer
            config={[
              { navigateTo: "/settings", breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_SETTINGS },
              { breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_ENROLLED_APPEARANCE }
            ]}
          />
        </BreadcrumbWrapper>
        <ErrorTemplate title={APPEARANCE_TEXT.FETCH_ERROR} description={error} testId="error-fetch-appearances" onRefresh={handleRefresh} />
      </AppearanceListContainer>
    );
  }

  // Render no license state
  if (!loading && !hasActiveLicenses) {
    return (
      <AppearanceListContainer>
        <BreadcrumbsContainer
          config={[
            { navigateTo: "/settings", breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_SETTINGS },
            { breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_ENROLLED_APPEARANCE }
          ]}
        />
        <Title size="medium">{APPEARANCE_TEXT.PAGE_TITLE}</Title>
        <NotificationWrapper>
          <Notification
            type="info"
            priority="normal"
            title={LICENSE_MESSAGES.NO_LICENSE_TITLE}
            subtitle={LICENSE_MESSAGES.NO_LICENSE_SUBTITLE}
            hideCloseButton={true}
            data-testid="no-license-notification"
          />
        </NotificationWrapper>
      </AppearanceListContainer>
    );
  }

  // Render empty state
  if (!loading && appearances.length === 0) {
    return (
      <AppearanceListContainer>
        <BreadcrumbWrapper>
          <BreadcrumbsContainer
            config={[
              { navigateTo: "/settings", breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_SETTINGS },
              { breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_ENROLLED_APPEARANCE }
            ]}
          />
        </BreadcrumbWrapper>
        <AppearanceListHeader>
          <Title size="Xlarge" bold data-testid="page-title">
            {APPEARANCE_TEXT.PAGE_TITLE}
          </Title>
          {standardizedUserRole !== ROLE_SITE_VIEWER && (
            <Button variant="primary" onClick={handleCreateAppearance} data-testid="create-appearance-button">
              {APPEARANCE_TEXT.CREATE_APPEARANCE}
            </Button>
          )}
        </AppearanceListHeader>
        <Body size="small" data-testid="page-subtitle">
          {APPEARANCE_TEXT.SUBTITLE}
        </Body>
        <EmptyState data-testid="empty-state">
          <EmptyStateTitle>{APPEARANCE_TEXT.NO_APPEARANCES}</EmptyStateTitle>
          <EmptyStateDescription>{APPEARANCE_TEXT.NO_APPEARANCES_DESC}</EmptyStateDescription>
        </EmptyState>
      </AppearanceListContainer>
    );
  }

  // Render appearances list
  return (
    <AppearanceListContainer data-testid="appearance-list-container">
      {/* Breadcrumbs */}
      <BreadcrumbWrapper>
        <BreadcrumbsContainer
          config={[
            { navigateTo: "/settings", breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_SETTINGS },
            { breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_ENROLLED_APPEARANCE }
          ]}
        />
      </BreadcrumbWrapper>
      <ErrorModal
        opened={!!deploymentBlockModal}
        title={
          deploymentBlockModal?.type === "delete" ? (
            <>
              {APPEARANCE_TEXT.DEPLOYMENT_DELETE_LOCK_TITLE}{" "}
              <i>
                <b>{deploymentBlockModal?.appearanceName}</b>
              </i>
            </>
          ) : (
            <>
              {APPEARANCE_TEXT.DEPLOYMENT_UNENROLL_LOCK_TITLE}{" "}
              <i>
                <b>{deploymentBlockModal?.appearanceName}</b>
              </i>
            </>
          )
        }
        body={
          deploymentBlockModal?.type === "delete" ? (
            <p>
              {APPEARANCE_TEXT.DEPLOYMENT_LOCK_BODY_BEFORE}
              <DeploymentLockLink
                onClick={() => {
                  setDeploymentBlockModal(null);
                  handleViewStreams(deploymentBlockModal?.appearance);
                }}
              >
                {APPEARANCE_TEXT.DEPLOYMENT_LOCK_LINK_TEXT}
              </DeploymentLockLink>
              {APPEARANCE_TEXT.DEPLOYMENT_DELETE_LOCK_BODY_AFTER}
            </p>
          ) : (
            <p>
              {APPEARANCE_TEXT.DEPLOYMENT_LOCK_BODY_BEFORE}
              <DeploymentLockLink
                onClick={() => {
                  setDeploymentBlockModal(null);
                  handleViewStreams(deploymentBlockModal?.appearance);
                }}
              >
                {APPEARANCE_TEXT.DEPLOYMENT_LOCK_LINK_TEXT}
              </DeploymentLockLink>
              {APPEARANCE_TEXT.DEPLOYMENT_LOCK_BODY_AFTER}
            </p>
          )
        }
        footerButtonText={CREATE_APPEARANCE.CLOSE}
        onClose={() => setDeploymentBlockModal(null)}
        ariaLabel="Deployment lock modal"
      />
      {showEnrolledSitesModal && enrolledSitesAppearance && (
        <DeployModal
          viewOnly={true}
          appearanceName={enrolledSitesAppearance.name}
          tagids={enrolledSitesAppearance.enrolledSiteIds}
          appearanceStatus={enrolledSitesAppearance.status}
          opened={showEnrolledSitesModal}
          closeModal={() => {
            setShowEnrolledSitesModal(false);
            setEnrolledSitesAppearance(null);
          }}
        />
      )}
      {showDeployModal && (
        <DeployModal
          appearanceName={currentAppearance.current.appearanceName}
          appearanceId={currentAppearance.current.appearanceId}
          tagids={getDeployModalTagids()}
          appearanceStatus={currentAppearance.current.appearanceStatus}
          deployments={currentAppearance.current.deployments}
          opened={showDeployModal}
          closeModal={() => setShowDeployModal(false)}
          onDeployBlockAttempt={() =>
            setDeploymentBlockModal({
              type: "unenroll",
              appearanceName: currentAppearance.current.appearanceName,
              appearance: appearanceList.find((a) => a.id === currentAppearance.current.appearanceId)
            })
          }
          onDeploySuccess={() => {
            if (crossSiteAppearance.current) {
              const app = crossSiteAppearance.current;
              crossSiteAppearance.current = null;
              dispatch(setSelectedAppearance(app));
              navigate(`${ROUTES.EDIT_APPEARANCE}?id=${app.id}`);
            } else {
              dispatch(fetchAppearancesRequest({}));
            }
          }}
        />
      )}
      {showCrossSiteWarning && (
        <CrossSiteWarningModal
          opened={showCrossSiteWarning}
          onCancel={() => {
            setShowCrossSiteWarning(false);
            crossSiteAppearance.current = null;
          }}
          onEnroll={handleCrossSiteEnroll}
        />
      )}
      {/* Header with Title and Create Button */}
      <AppearanceListHeader>
        <Title size="large" data-testid="page-title">
          {APPEARANCE_TEXT.PAGE_TITLE}
        </Title>
        {standardizedUserRole !== ROLE_SITE_VIEWER && (
          <Button variant="primary" onClick={handleCreateAppearance} data-testid="create-appearance-button">
            {APPEARANCE_TEXT.CREATE_APPEARANCE}
          </Button>
        )}
      </AppearanceListHeader>

      {/* Subtitle */}
      <SubtitleRow>
        <Body size="small" id="ea-page-subtitle" data-testid="page-subtitle">
          {APPEARANCE_TEXT.SUBTITLE}
        </Body>
        <LegendContainer>
          {LEGEND_ITEMS.map(({ status, label, icon }) => (
            <LegendItem key={status}>
              <LegendIconBadge color={IMAGE_STATUS_ICON_BG_COLORS[status]}>
                <Icon name={icon} size="small" color="#FFFFFF" ariaHidden={true} />
              </LegendIconBadge>
              {label}
            </LegendItem>
          ))}
        </LegendContainer>
      </SubtitleRow>

      {standardizedUserRole === ROLE_SITE_MANAGER && (
        <TabsWrapper>
          <Tabs orientation="horizontal" indicatorPosition="bottom" fillContainer={false}>
            <Tab label={APPEARANCE_TEXT.MY_EAS_TAB} onClick={() => setActiveTab(0)} />
            <Tab label={APPEARANCE_TEXT.OTHER_EAS_TAB} onClick={() => setActiveTab(1)} />
          </Tabs>
        </TabsWrapper>
      )}

      {!filteredAppearances?.length ? (
        <EmptyState data-testid="empty-state">
          <EmptyStateTitle>{APPEARANCE_TEXT.NO_APPEARANCES}</EmptyStateTitle>
          <EmptyStateDescription>{APPEARANCE_TEXT.NO_APPEARANCES_DESC}</EmptyStateDescription>
        </EmptyState>
      ) : (
        <>
          {/* Appearance List */}
          <AppearanceGrid data-testid="appearance-grid">
            {filteredAppearances?.map((appearance) => {
              const { showErrorBadge, errorCount, badgeTextColor, badgeLabel, tooltipText, badgeBg, secondaryBadge } = getAppearanceBadgeProps(appearance);
              return (
                <StyledAppearanceCard key={appearance.id} data-testid={`appearance-item-${appearance.id}`}>
                  <LazyImageFetcher appearanceId={appearance.id} images={appearance.images} />
                  {/* Header: Title, Status Badge, and Actions */}
                  <AppearanceCardHeader>
                    <AppearanceLeftSection>
                      <Body size="large" bold data-testid="page-subtitle">
                        {appearance.name || APPEARANCE_TEXT.DEFAULT_APPEARANCE_NAME}
                      </Body>
                      <AppearanceBadgesRow>
                        {badgeLabel && (
                          <MuiTooltip title={tooltipText || ""} placement="bottom" arrow disableHoverListener={!tooltipText}>
                            <ImageStatusBadge
                              status={appearance.status}
                              isError={showErrorBadge}
                              style={badgeBg ? { backgroundColor: badgeBg } : undefined}
                              data-testid="appearance-status"
                            >
                              <Body size="small" bold color={badgeTextColor}>
                                {badgeLabel}
                              </Body>
                            </ImageStatusBadge>
                          </MuiTooltip>
                        )}
                        {secondaryBadge?.badgeLabel && (
                          <MuiTooltip title={secondaryBadge.tooltipText || ""} placement="bottom" arrow disableHoverListener={!secondaryBadge.tooltipText}>
                            <ImageStatusBadge
                              status={appearance.status}
                              isError={false}
                              style={secondaryBadge.badgeBg ? { backgroundColor: secondaryBadge.badgeBg } : undefined}
                            >
                              <Body size="small" bold color={secondaryBadge.badgeTextColor}>
                                {secondaryBadge.badgeLabel}
                              </Body>
                            </ImageStatusBadge>
                          </MuiTooltip>
                        )}
                        {appearance.status !== APPEARANCE_RUNTIME_STATUS.NEW && isCreatedWithin24Hours(appearance) && (
                          <ImageStatusBadge status={APPEARANCE_RUNTIME_STATUS.NEW} isError={false}>
                            <Body size="small" bold color={STATUS_BADGE_TEXT_COLORS[APPEARANCE_RUNTIME_STATUS.NEW]}>
                              {APPEARANCE_RUNTIME_STATUS.NEW}
                            </Body>
                          </ImageStatusBadge>
                        )}
                      </AppearanceBadgesRow>
                    </AppearanceLeftSection>
                    <AppearanceActions>
                      {(standardizedUserRole === ROLE_ADMIN || (standardizedUserRole !== ROLE_SITE_VIEWER && isMyEA(appearance))) && (
                        <div style={{ cursor: "pointer" }} onClick={() => handleDelete(appearance.id)}>
                          <Icon
                            data-testid="delete-appearance-button"
                            title={APPEARANCE_TEXT.DELETE_APPEARANCE_TITLE}
                            name="trash"
                            size="large"
                            color="#000000"
                            ariaLabel="Delete appearance"
                          />
                        </div>
                      )}
                      {standardizedUserRole !== ROLE_SITE_VIEWER && (
                        <Button
                          disabled={checkIsImagesLoading(appearance.id) || (appearance.images || []).some((img) => retryingImageIds?.[img.imageId])}
                          use="secondary"
                          size="small"
                          onClick={() => handleEdit(appearance)}
                          data-testid="edit-appearance-button"
                        >
                          {APPEARANCE_TEXT.EDIT_BUTTON}
                        </Button>
                      )}
                      {standardizedUserRole !== ROLE_SITE_VIEWER && (
                        <Button
                          disabled={
                            checkIsImagesLoading(appearance.id) || appearance.status === APPEARANCE_RUNTIME_STATUS.INVALID || !appearance.images?.length
                          }
                          use="secondary"
                          size="small"
                          onClick={() => {
                            setShowDeployModal(true);
                            currentAppearance.current.appearanceName = appearance.name;
                            currentAppearance.current.appearanceId = appearance.id;
                            currentAppearance.current.enrolledSiteIds = appearance?.enrolledSiteIds || [];
                            currentAppearance.current.appearanceStatus = appearance?.status || "";
                            currentAppearance.current.deployments = appearance?.deployments || [];
                            currentAppearance.current.isOtherEA = !isMyEA(appearance);
                          }}
                          data-testid="enroll-appearance-button"
                        >
                          {APPEARANCE_TEXT.ENROLL_BUTTON}
                        </Button>
                      )}
                    </AppearanceActions>
                  </AppearanceCardHeader>

                  {/* Images Row */}
                  {renderAppearanceImagesSection(appearance)}

                  {/* View Links Row — hidden for site manager on Other EAs */}
                  {!(standardizedUserRole === ROLE_SITE_MANAGER && !isMyEA(appearance)) && (
                    <ViewStreamsRow>
                      {/* View Streams Link */}
                      {(appearance.deployments?.length ?? 0) > 0 ? (
                        <ViewStreamsLink role="button" onClick={() => handleViewStreams(appearance)}>
                          <Body size="small">{APPEARANCE_TEXT.VIEW_STREAMS}</Body>
                        </ViewStreamsLink>
                      ) : (
                        <Body size="small" color="#6f7171">
                          {APPEARANCE_TEXT.NO_ACTIVE_DEPLOYMENTS}
                        </Body>
                      )}
                      {/* View Enrolled sites Link */}
                      {(appearance.enrolledSiteIds?.length ?? 0) > 0 ? (
                        <ViewStreamsLink
                          role="button"
                          onClick={() => {
                            setEnrolledSitesAppearance(appearance);
                            setShowEnrolledSitesModal(true);
                          }}
                        >
                          <Body size="small">{APPEARANCE_TEXT.VIEW_ENROLLED_SITES}</Body>
                        </ViewStreamsLink>
                      ) : (
                        <Body size="small" color="#6f7171">
                          {APPEARANCE_TEXT.NOT_ENROLLED_TO_ANY_SITE}
                        </Body>
                      )}
                    </ViewStreamsRow>
                  )}
                </StyledAppearanceCard>
              );
            })}
          </AppearanceGrid>

          {/* Sites Section */}
          {/* <SitesSection>
                <SitesIntroText size="large">
                  {SITES_TEXT.INTRO}
                </SitesIntroText>
                <SitesList>
                  {getAllUniqueSiteNames().map((siteName, index) => (
                    <SiteTag key={index}>
                      <input
                        type="checkbox"
                        id={`site-${index}`}
                        checked={selectedSites.has(siteName)}
                        onChange={() => handleSiteToggle(siteName)}
                      />
                      <label 
                        htmlFor={`site-${index}`}
                      >
                        {siteName}
                      </label>
                    </SiteTag>
                  ))}
                </SitesList>
              </SitesSection> */}
        </>
      )}
      {/* Image Preview Modal with Carousel */}
      <Modal opened={isImageModalOpen} onClose={handleCloseImageModal} width="663px" maxWidth="663px" maxHeight="610px" data-testid="image-preview-modal">
        <ModalHeader>
          <Title size="large" bold data-testid="image-modal-title">
            {imageModalTitle}
          </Title>
        </ModalHeader>

        {selectedImages.length > 0 && (
          <SlickImageCarousel
            images={selectedImages}
            currentIndex={currentImageIndex}
            onChange={setCurrentImageIndex}
            placeholderImage={PLACEHOLDER_IMAGE}
            imageWidth={441}
            imageHeight={454}
            arrowOffset={50}
            arrowSize={36}
            renderErrorSlide={() => (
              <div style={{ width: 441, height: 454, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ImageDownloadErrorWrapper>
                  <ImageDownloadErrorContent>
                    <Icon name="error" size="small" color="#6f7171" />
                    <span>{APPEARANCE_TEXT.IMAGE_FAILED_TO_LOAD}</span>
                  </ImageDownloadErrorContent>
                </ImageDownloadErrorWrapper>
              </div>
            )}
            settings={{
              dots: false,
              infinite: false,
              speed: 300,
              autoplay: false,
              autoplaySpeed: 3000
            }}
          />
        )}
      </Modal>

      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        appearanceName={appearanceToDelete?.name}
        loading={deleteLoading}
      />
      <ImageGuidelinesModal opened={isGuidelinesOpen} onClose={() => setIsGuidelinesOpen(false)} />
    </AppearanceListContainer>
  );
};

export default EAList;
